import { Check } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { Container, PageHero, CTASection } from "@/components/primitives";
import { SERVICES } from "@/data/site";

const PROCESS = [
  {
    n: "01",
    title: "Understand",
    body: "We start with the business problem and the people it affects, not the technology.",
  },
  {
    n: "02",
    title: "Design",
    body: "Architecture and product decisions made deliberately, with trade-offs made explicit.",
  },
  {
    n: "03",
    title: "Build",
    body: "Production-quality software, shipped incrementally, engineered to last and to maintain.",
  },
  {
    n: "04",
    title: "Support",
    body: "Reliable delivery and honest guidance as the product and the business evolve.",
  },
];

export default function Services() {
  return (
    <>
      <SEO
        title="Services"
        description="How Vivek Chandra helps businesses build better software: custom business websites, modern web applications, AI-powered solutions and technical consulting, all framed around business outcomes."
        path="/services"
      />

      <PageHero
        label="Services"
        title="How I help businesses build better software."
      >
        I work with a small number of clients at a time, as a hands-on engineer
        and technical partner. Engagements are framed around business outcomes,
        not billable hours or technology checklists.
      </PageHero>

      <section className="py-20 md:py-28" data-testid="services-list">
        <Container>
          <div className="grid grid-cols-1 gap-px overflow-hidden border border-white/10 lg:grid-cols-2">
            {SERVICES.map((s) => (
              <Reveal
                key={s.slug}
                className="bg-ink-base p-8 md:p-12"
                data-testid={`service-${s.slug}`}
              >
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-accent">
                  {s.index}
                </span>
                <h2 className="mt-6 font-cabinet text-2xl font-medium tracking-tight text-primary-ink md:text-3xl">
                  {s.title}
                </h2>
                <p className="mt-3 text-lg leading-relaxed text-primary-ink">
                  {s.outcome}
                </p>
                <p className="mt-4 leading-relaxed text-secondary-ink">
                  {s.body}
                </p>
                <ul className="mt-8 space-y-3">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-center gap-3">
                      <Check className="h-4 w-4 shrink-0 text-accent" />
                      <span className="text-secondary-ink">{p}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section className="pb-24 md:pb-32" data-testid="process">
        <Container>
          <Reveal className="max-w-2xl">
            <h2 className="font-cabinet text-3xl font-medium tracking-tight text-primary-ink md:text-4xl">
              A simple, senior way of working.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-px overflow-hidden border border-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map((p) => (
              <Reveal key={p.n} className="bg-ink-base p-8">
                <span className="font-mono text-3xl font-medium text-accent">
                  {p.n}
                </span>
                <h3 className="mt-6 font-cabinet text-xl font-medium tracking-tight text-primary-ink">
                  {p.title}
                </h3>
                <p className="mt-3 leading-relaxed text-secondary-ink">
                  {p.body}
                </p>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <CTASection />
    </>
  );
}
